import { type QueryClient } from "@tanstack/react-query";
import { type AuthUser, fetchAuthSession, getCurrentUser } from "aws-amplify/auth";

import { parseBranchIdsClaim, parseClientIdsClaim, parseGroupsClaim } from "@recobra/core";

const authQueryKey = ["auth"] as const;

const fetchAuth = async (): Promise<{
  user: AuthUser;
  cognitoGroups: string[];
  clientIds: string[];
  branchIds: string[];
  displayName?: string;
  email?: string;
  picture?: string;
}> => {
  const user = await getCurrentUser();
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;
  if (!idToken) {
    throw new Error("Authenticated user is missing an ID token.");
  }
  const payload = idToken.payload;

  return {
    user,
    cognitoGroups: parseGroupsClaim(payload["cognito:groups"]),
    clientIds: parseClientIdsClaim(payload["custom:clientIds"]),
    branchIds: parseBranchIdsClaim(payload["custom:branchIds"]),
    displayName: readStringClaim(payload, "name"),
    email: readStringClaim(payload, "email"),
    picture: readStringClaim(payload, "picture"),
  };
};

export const authQueryOptions = {
  queryKey: authQueryKey,
  queryFn: fetchAuth,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/** Refetch Cognito ID token claims after profile or attribute changes. */
export const invalidateAuthSession = async (queryClient: QueryClient): Promise<void> => {
  await fetchAuthSession({ forceRefresh: true });
  await queryClient.invalidateQueries({ queryKey: authQueryKey });
};

const readStringClaim = (
  payload: Record<string, unknown> | undefined,
  key: string,
): string | undefined => {
  const value = payload?.[key];
  if (typeof value !== "string" || value.length === 0) {
    return undefined;
  }

  return value;
};
